import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import { RiSendPlaneLine } from "@remixicon/react";
import { TicketMessage, useSendMessagesMutation } from "../../../__generated__/api";
import {
    ActionType, ActionHandler, ActionContext, HANDLER_INTERFACE,
} from "../../../contexts/ticket-state/action-registry";
import { TicketItem, useRegisterAction, useTicketState } from "../../../contexts/ticket-state";
import Editor from "../../agent-v3/editor/Editor";
import Confirm from "../../confirm/Confirm";
import TimedToast from "../../timed-toast/TimedToast";
import Styles from "./send-messages.module.scss";

export default function SendMessages() {
    const ticketState = useTicketState();

    const editorRef = React.createRef<ReactQuill>();
    const [tickets, setTickets] = useState<TicketItem[]>([]);
    const [actionHandler, setActionHandler] = useState<ActionHandler>();
    const [isConfirming, setIsConfirming] = useState(false);
    const [shouldShow, setShouldShow] = useState(false);
    const [body, setBody] = useState("");

    const [sendMessages] = useSendMessagesMutation();

    useEffect(() => {
        if (shouldShow) {
            editorRef.current?.focus();
        }
    }, [shouldShow]);

    const send = (selectedTickets?: TicketItem[]) => {
        setTickets(selectedTickets ?? ticketState.selectionState.current);
    };

    const doSend = async () => {
        const ticketIndex = tickets.toIdMap(item => item.ticket.id);

        const result = await sendMessages({
            variables: { chatIds: tickets.toIdList(), message: { body } },
        });

        actionHandler?.done();
        result.data?.sendMessages?.forEach(message => {
            ticketIndex[message.ticketId]?.appendMessages(message as TicketMessage);
        });

        setBody("");
        setShouldShow(false);
        setIsConfirming(false);
    };

    const doCancel = () => {
        actionHandler?.rollback();
        setShouldShow(false);
        setTickets([]);
        setBody("");
    };

    useRegisterAction({
        createBulkActionButton(_items, context) {
            return context === ActionContext.BULK_ACTION ? {
                text: "Send Message",
                icon: <RiSendPlaneLine />,
                variant: "ghost-gray",
            } : undefined;
        },
        handleBulkAction(handler) {
            if (handler.context === ActionContext.TICKET_ACTION) {
                handler.items.forEach(item => item.closeIfOpen());
            }

            send(handler.items);
            setShouldShow(true);
            setActionHandler(handler);
        },
    }, ActionType.SEND_MESSAGES);

    return (
        <>
            <Confirm
                confirmText="Confirm"
                shouldShow={shouldShow}
                closeText="Close"
                title="Send Bulk Message"
                onHide={() => doCancel()}
                onConfirm={() => {
                    setIsConfirming(true);
                    setShouldShow(false);
                }}
            >
                <Editor ref={editorRef} onChange={change => setBody(change)} value={body} className={Styles.editor} />
            </Confirm>
            <TimedToast
                canCancel
                shouldShow={isConfirming}
                title="Sending Messages"
                seconds={5}
                onClose={doSend}
                onCancel={() => {
                    setIsConfirming(false);
                    setShouldShow(true);
                }}
            >
                <div>
                    Sending a message to
                    {" "}
                    {tickets.length}
                    {" "}
                    tickets.
                </div>
            </TimedToast>
        </>
    );
}

HANDLER_INTERFACE.register(SendMessages);
